/* eslint-disable react/no-unused-class-component-methods  */
/* eslint-disable react/sort-comp  */

import * as React from 'react';
import { PureComponent } from 'react';
import {
    Canvas, CanvasEvents, IText, ITextProps, TEvent, FabricImage, ImageProps,
    FabricObject, loadSVGFromURL,
} from 'fabric';
import { SketchProperties, SketchState } from '../../types';
import { create_tool } from './tools/factory';
import { EventCallback, FabricCanvasEventsManager } from './events';
import { CanvasEventAware, FabricCanvasTool } from './tools';
import { uuid4 } from '../../utils';
import UndoHistory from './history';

/**
 * Sketch Tool based on FabricJS for React Applications
 */
class SketchField extends PureComponent<SketchProperties, SketchState> {
    private canvas!: Canvas | null;

    private canvasEl!: HTMLCanvasElement | null;

    private containerEl!: HTMLDivElement | null;

    private tool!: FabricCanvasTool;

    public history!: UndoHistory;

    private clipboard: FabricObject | null = null;

    private resizeObserver!: ResizeObserver;

    public hasSelection: boolean = false;

    public width: number = 0;

    public height: number = 0;

    public zoomFactor: number = 1;

    // eslint-disable-next-line react/static-property-placement
    static defaultProps: SketchProperties = {
        tool: 'pencil',
        lineColor: 'black',
        lineWidth: 1,
        onChange: () => {},
    };

    constructor(props: SketchProperties) {
        super(props);
        this.height = props.height || 0;
        this.width = props.width || 0;
        this.history = new UndoHistory(25);
        this.resizeObserver = new ResizeObserver(entries => {
            window.requestAnimationFrame((): void => {
                if (!Array.isArray(entries) || !entries.length) {
                    return;
                }
                if (!this.canvas || !this.containerEl) {
                    return;
                }
                // Calculate the aspect ratios
                this.width = this.width || this.canvas.getWidth();
                this.height = this.height || this.canvas.getHeight();
                this.zoomFactor = this.getScaleFactor(this.width, this.height);
                this.zoom(1);
            });
        });
        this.state = {
            // eslint-disable-next-line react/no-unused-state
            action: true,
        };
    }

    componentDidMount() {
        this.canvas = new Canvas(this.canvasEl!);
        this.canvas.backgroundColor = 'white';
        this.canvas.renderAll();
        this.tool = create_tool(this.canvas, this.props);

        if (this.containerEl) {
            this.resizeObserver.observe(this.containerEl);
        }

        // Bind Events to Actions
        this._bind_canvas_events(new FabricCanvasEventsManager(this.canvas, this));
        this._bind_canvas_events(this.history);
    }

    componentDidUpdate(
        prevProps: Readonly<SketchProperties>,
        prevState: Readonly<SketchState>,
        snapshot?: any,
    ): void {
        const { props } = this;
        const { state } = this;

        if (this.canvas) {
            this.tool = create_tool(this.canvas, {
                ...props,
                width: props.width || this.width,
                height: props.height || this.height,
                lineWidth: (props.lineWidth || 1),
            });
        }
    }

    componentWillUnmount() {
        if (this.containerEl) {
            this.resizeObserver.unobserve(this.containerEl);
        }
    }

    /**
     * Loads the content of an SVG into the canvas
     *
     * @param dataUrl the dataUrl to be used as a background
     * @param options
     */
    public async loadSvg(dataUrl) {
        const { canvas } = this;
        if (!canvas || !this.containerEl) {
            return;
        }

        const loadedSVG = await loadSVGFromURL(dataUrl, (_element, obj) => {
            if (obj) {
                // @todo: we need to handle the case on long term when we may have multiple images in the SVG
                if (obj.type === 'image') {
                    canvas.backgroundImage = obj;
                } else if (!(obj.type === 'rect' && obj.width === 100 && obj.height === 100)) {
                    //
                    // Saving to sgv in fabric results an empty rect to be created with width and height of 100
                    // to be used as a background, we need to remove it otherwise subsequent save will result in
                    // multiple background rects
                    //
                    // Yeah, De Morgan's law could be used to simplify the condition above but it is more clear in this way
                    //
                    canvas.add(obj);
                }
            }
        });

        if (loadedSVG) {
            this.history.clear();
            this.height = loadedSVG.options.height;
            this.width = loadedSVG.options.width;
            this.zoomFactor = this.getScaleFactor(this.width, this.height);
            this.zoom(1);
            canvas.renderAll();
        }
    }

    /**
     * Sets the background from the dataUrl given
     *
     * @param dataUrl the dataUrl to be used as a background
     * @param options
     */
    public async setBackgroundFromDataUrl(dataUrl, options: Partial<ImageProps> & {
        stretched?: boolean, stretchedX?: boolean, stretchedY?: boolean, resizeCanvas?: boolean,
    } = {}) {
        const { canvas } = this;
        const {
            stretched, stretchedX, stretchedY, resizeCanvas, ...fabricOptions
        } = options;
        if (!canvas) {
            return;
        }
        canvas.setDimensions({ width: 0, height: 0 });
        canvas.renderAll();
        const imgObj = await FabricImage.fromURL(dataUrl, { crossOrigin: 'anonymous' });
        if (imgObj && this.containerEl) {
            if (resizeCanvas) {
                this.height = imgObj.height;
                this.width = imgObj.width;
                this.zoomFactor = this.getScaleFactor(this.width, this.height);
                this.zoom(1);
            } else {
                if (stretched || stretchedX) imgObj.scaleToWidth(canvas.width);
                if (stretched || stretchedY) imgObj.scaleToHeight(canvas.height);
            }
            //
            // We need to convert the image to a dataUrl to avoid the tainted canvas issue
            //
            canvas.backgroundImage = await FabricImage.fromURL(imgObj.toDataURL());
            canvas.renderAll();
        }
    }

    /**
     * Zoom the drawing by the factor specified
     *
     * The zoom factor is a percentage with regards the original, for example if factor is set to 2
     * it will double the size whereas if it is set to 0.5 it will half the size
     *
     * @param factor the zoom factor
     */
    zoom = factor => {
        const { canvas } = this;
        const { onChange } = this.props;
        this.zoomFactor *= factor;
        if (canvas && this.containerEl) {
            canvas.setZoom(this.zoomFactor);
            canvas.setDimensions({ width: this.width * this.zoomFactor, height: this.height * this.zoomFactor });
        }

        if (onChange) {
            onChange();
        }
    };

    getScaleFactor = (
        width: number,
        height: number,
    ): number => {
        const { canvas, containerEl: container } = this;
        if (!canvas || !container) {
            return 1;
        }
        const { offsetWidth, clientHeight } = container;
        if (height === 0 || clientHeight === 0) {
            console.warn('Height or container clientHeight is zero, cannot calculate aspect ratio.');
            return 1;
        }
        const containerAspectRatio = offsetWidth / clientHeight;
        const targetAspectRatio = width / height;

        // Determine the scaling factor to fit the image within the canvas while maintaining the image's aspect ratio
        return containerAspectRatio < targetAspectRatio ? offsetWidth / width : clientHeight / height;
    };

    public rotate90(direction: 1 | -1) {
        const new_w = this.height;
        const new_h = this.width;
        this.height = new_h;
        this.width = new_w;
        this.zoom(1);
        this.rotate(direction * 90);
    }
    
    public rotate(angleChange: number) {
        if (!this.canvas) {
            return;
        }
        const { canvas } = this;
        const { onChange } = this.props;

        [...canvas.getObjects(), ...(canvas.backgroundImage ? [canvas.backgroundImage] : [])].forEach(obj => {
            const currentAngle = obj.get('angle');
            const val = currentAngle + angleChange;
            obj.set('angle', val);

            const posVal = {
                top: obj.get('top'),
                left: obj.get('left'),
            };

            const newLeft = angleChange >= 0 ? this.width - posVal.top : posVal.top;
            const newTop = angleChange >= 0 ? posVal.left : this.height - posVal.left;

            obj.set('top', newTop);
            obj.set('left', newLeft);
            obj.setCoords();
        });

        canvas.renderAll();
        if (onChange) {
            onChange();
        }
    }

    public addText(text, options: Partial<ITextProps> = {}) {
        if (!this.canvas) {
            return;
        }
        const { onChange } = this.props;
        const iText = new IText(text, options);
        const opts = {
            left: (this.canvas.getWidth() - iText.width) * 0.5,
            top: (this.canvas.getHeight() - iText.height) * 0.5,
        };
        Object.assign(options, opts);
        iText.set({
            left: options.left,
            top: options.top,
        });

        this.canvas.add(iText);
        this.canvas.setActiveObject(iText);
        iText.enterEditing();
        iText.selectAll();
        if (onChange) {
            onChange();
        }
    }

    /**
     * Perform an undo operation on canvas, if it cannot undo it will leave the canvas intact
     */
    undo() {
        const { history } = this;
        const { onChange } = this.props;
        const [obj, prevState, currState] = history.getCurrent();
        history.undo();
        if (obj.__removed) {
            // eslint-disable-next-line react/no-unused-state
            this.setState({ action: false }, () => {
                this.canvas!.add(obj);
                obj.__version -= 1;
                obj.__removed = false;
            });
        } else if (obj.__version <= 1) {
            this.canvas!.remove(obj);
        } else {
            obj.__version -= 1;
            obj.setOptions(JSON.parse(prevState));
            obj.setCoords();
            this.canvas!.renderAll();
        }
        if (onChange) {
            onChange();
        }
    }

    /**
     * Perform a redo operation on canvas, if it cannot redo it will leave the canvas intact
     */
    redo() {
        const { history } = this;
        const { onChange } = this.props;
        if (history.canRedo()) {
            const canvas = this.canvas!;
            const [obj, prevState, currState] = history.redo();
            if (obj.__version === 1) {
                // eslint-disable-next-line react/no-unused-state
                this.setState({ action: false }, () => {
                    canvas.add(obj);
                    obj.__version = 1;
                });
            } else {
                obj.__version += 1;
                obj.setOptions(JSON.parse(currState));
            }
            obj.setCoords();
            canvas.renderAll();
            if (onChange) {
                onChange();
            }
        }
    }

    async copy() {
        const selected = this.canvas?.getActiveObject();
        if (selected) {
            const cloned = await selected.clone();
            this.clipboard = cloned;
        }
    }

    async paste() {
        // clone again, so you can do multiple copies.
        if (!this.clipboard) {
            return;
        }
        const { onChange } = this.props;
        const clonedObj = await this.clipboard!.clone();
        if (clonedObj) {
            const canvas = this.canvas!;
            canvas.discardActiveObject();
            clonedObj.set({
                left: clonedObj.left + 10,
                top: clonedObj.top + 10,
                evented: true,
            });
            if (clonedObj.type === 'activeSelection') {
                // active selection needs a reference to the canvas.
                // @ts-ignore
                clonedObj.canvas = canvas;
                // @ts-ignore
                clonedObj.forEachObject(obj => canvas.add(obj));
                clonedObj.setCoords();
            } else {
                canvas.add(clonedObj);
            }
            this.clipboard!.top += 10;
            this.clipboard!.left += 10;
            canvas.setActiveObject(clonedObj);
            canvas.requestRenderAll();
            if (onChange) {
                onChange();
            }
        }
    }

    /**
     * Remove selected object from the canvas
     */
    removeSelected() {
        const canvas = this.canvas!;
        const { onChange } = this.props;
        const activeObj = canvas.getActiveObjects();
        if (activeObj.length > 0) {
            activeObj.forEach(obj => {
                // @ts-ignore
                obj.__removed = true;
                const objState = obj.toJSON();
                // @ts-ignore
                obj.__originalState = objState;
                const state = JSON.stringify(objState);
                this.history.keep([obj, state, state]);
                canvas.remove(obj);
            });
            canvas.discardActiveObject();
            canvas.requestRenderAll();
            if (onChange) {
                onChange();
            }
        }
    }

    changeSeletedObject(property: string | Record<string, any>, value?: any) {
        const canvas = this.canvas!;
        const activeObj = canvas.getActiveObjects();
        if (activeObj.length > 0) {
            activeObj.forEach(obj => {
                const objState = obj.toJSON();
                // @ts-ignore
                obj.__originalState = objState;
                obj.set(property, value);
                const state = obj.toJSON();
                this.history.keep([obj, objState, state]);
            });
            canvas.requestRenderAll();
        }
    }

    public async getDataURL() {
        if (this.canvas) {
            if (this.canvas.backgroundImage) {
                this.canvas.backgroundColor = '';
            }
            const svgString = await this.canvas.toSVG();
            this.canvas.backgroundColor = 'white';
            return svgString;
        }
        return null;
    }

    /**
   * Bind Canvas events with Component
   */
    private _bind_canvas_events(eventAware: CanvasEventAware): void {
    // Helper declaration for all canvas events
        const on = (name: keyof CanvasEvents, callback: EventCallback): void => {
            // @ts-ignore
            this.canvas?.on(name, (event: TEvent) => {
                callback.call(eventAware, {
                    event,
                    tool: this.tool,
                    props: this.props,
                    state: this.state,
                });
            });
        };
        // Fabric Events
        on('mouse:up', eventAware.onMouseUp);
        on('mouse:down', eventAware.onMouseDown);
        on('mouse:move', eventAware.onMouseMove);
        on('mouse:out', eventAware.onMouseOut);
        on('object:added', eventAware.onObjectAdded);
        on('object:moving', eventAware.onObjectMoving);
        on('object:modified', eventAware.onObjectModified);
        on('object:removed', eventAware.onObjectRemoved);
        on('object:scaling', eventAware.onObjectScaling);
        on('object:rotating', eventAware.onObjectRotating);
        on('text:changed', eventAware.onTextChanged);
        on('text:editing:exited', eventAware.onTextEditingExited);
        on('text:editing:entered', eventAware.onTextEditingEntered);
        on('text:selection:changed', eventAware.onTextSelectionChanged);
        on('selection:updated', eventAware.onSelectionChanged);
        on('selection:created', eventAware.onSelectionCreated);
        on('selection:cleared', eventAware.onSelectionCleared);
    }

    render() {
        const {
            className, style, width, height,
        } = this.props;
        const canvasDivStyle = {
            ...(style || { border: 'solid 1px ', backgroundColor: 'lightgray', overflow: 'scroll' }),
            width: '100%',
            height: '100%',
        };
        return (
            <div
                id={uuid4()}
                className={className}
                style={canvasDivStyle}
                ref={elem => { this.containerEl = elem; }}
            >
                <canvas id={uuid4()} ref={elem => { this.canvasEl = elem; }}>
                    Sorry, Canvas HTML5 element is not supported by your browser :(
                </canvas>
            </div>
        );
    }
}

export default SketchField;
